<template>
	<div class="block-editor-container">
		<div class="webhook-container">
			<BlockEditorContext :hasEditPermissions="hasEditPermissions" />
		</div>

		<div v-if="showBottomBar">
			<BlockEditorBottomBar
				:isTestingWidget="false"
				:loadingTestAndContinue="loadingTestAndContinue"
				:showTestResultModal.sync="isShowTestResultModal"
				:btnDisabled="btnDisabled"
				:testResponse="testResponse"
				:testResultStatus="testResultStatus"
				:showParams="isShowParams"
				:isCloudFlow="isCloudFlow"
				:hasEditPermissions="hasEditPermissions"
				:triggerConfig="triggerConfig"
				:btnFuncLoading="isBtnFuncLoading"
				@btnFunc="btnFunc"
				@skipTest="skipTestNode"
			/>
		</div>
		<div class="test-modal narrow-widget-max-width" :class="{ 'is-visible': isShowTestResultModal }" v-if="showTestResultPopUp">
			<TestResultModal :isError="isTestError" @onClose="hideTestCard">
				<TestResultsDataProvider
					:showTestResultModal.sync="isShowTestResultModal"
					:testBtnLoading="isTriggerSetupLoading"
					@test-again="applyTestAgain"
				/>
			</TestResultModal>
			<div v-if="loadingTestAndContinue">
				<Loader />
			</div>
		</div>
		<div class="editor-overlay" :class="{ show: isShowTestResultModal }" @click="hideTestCard">
			<Loader v-if="loadingTestAndContinue" />
		</div>
	</div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapActions, mapGetters } from 'vuex';

/* Components */
import BlockEditorBottomBar from './BlockEditorBottomBar';
import BlockEditorContext from './BlockEditorContext';
import TestResultModal from './TestResultModal';
import BlockEditorService from './block-editor-service';
import TestResultsDataProvider from './helpers/TestResultsDataProvider';
import { openTestURLToolDialog } from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getAction, getActionParams, getIfActionHasDynamicParams } from '@/store/modules/actions/getters';
import { getActionsOfApp } from '@/store/modules/apps/actions';
import { getService } from '@/store/modules/apps/getters';
import { getContext } from '@/store/modules/contexts/getters';
import { getAccountConnection } from '@/store/modules/credentials/getters';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { getNodeData, testStep as nodeTestStep, skipTest as nodeSkipTest } from '@/store/modules/nodes/actions';
import {
	getNodeTestResult,
	getNode,
	loadingTestAndContinue as loadingTestAndContinueGetter,
	getNodeOutputLabelsByNodeId,
	useIsRootNode,
} from '@/store/modules/nodes/getters';
import { getPlacement } from '@/store/modules/placements/getters';
import {
	testStep as selectedNodeTestStep,
	skipTest as selectedNodeSkipTest,
	setSideBarState,
	setEditNodeID,
	triggerSetup as selectedNodeTriggerSetup,
} from '@/store/modules/selectedNode/actions';
import {
	isNodeDataLoading,
	isTestingProfile,
	flatPreviousSteps,
	editNode,
	nodeType,
	isWebhook as isWebhookGetter,
	isScheduledWebhook as isScheduledWebhookGetter,
	isBranches as isBranchesGetter,
	isSendNotification,
	isEmailTemplates as isEmailTemplatesGetter,
	isBranchesConsolidateDataNode as isBranchesConsolidateDataNodeGetter,
	editNodeParams,
	editNodeParamsId,
	notification,
	isTriggerNode,
} from '@/store/modules/selectedNode/getters';
import {
	rootNode,
	actionContextNode,
	element,
	isNativeElement,
	editedWorkflowVersion,
	isOfTypeCloud,
	elementContextualData,
	isOfTypeFeature,
	isOfTypeTool,
	selectedWorkflowId,
	editWorkflow,
	isCustomerAuthOn,
} from '@/store/modules/selectedWorkflow/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { fetchIsWaitingForSampleData as getWorkflowWaitingForSampleData } from '@/store/modules/workflows/actions';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { flattenPreserveArrays } from '@/utils/helpers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import { WAITING_FLAG_TYPES } from '@/utils/workflow-service.const';
import WorkflowToolService from '@/utils/workflow-tool-service';
export default {
	name: 'WebhookBlockEditor',
	components: {
		BlockEditorContext,
		BlockEditorBottomBar,
		TestResultModal,
		TestResultsDataProvider,
	},
	props: {
		nodeID: { type: [Number, String] },
		accordionMenuItem: { type: Object },
	},
	data() {
		return {
			isShowParams: null,
			isShowTestResultModal: false,
			isShowAnimation: false,
			isTestedDataChanged: false,
			isBtnFuncLoading: false,
			isWaitingForSampleData: false,
			isChangeAction: false,
			isTriggerSetupLoading: false,
			// hasTestParams: false,
			timer: null,
		};
	},
	async mounted() {
		await this[setEditNodeID.fullPath]({ id: this.nodeID });
		this.initShowParams();
	},
	watch: {
		testResponse(n, o) {
			// This watcher job is to detect new testResponse in case of the first node in cloud automation
			const currentNode = this.getSourceOfRelevantNode();
			const isRootNode = currentNode && this.useIsRootNode(currentNode.id);
			const isAutomationTrigger = this.isTriggerNode && this.triggerConfig?.subscribed;
			const newResponse = n && n != o && typeof n === 'object' && Object.keys(n).length > 0;
			if (this.isCloudFlow && (this.isWebhook || isAutomationTrigger) && isRootNode && newResponse && this.isWaitingForSampleData) {
				this.isWaitingForSampleData = false; // we no longer wait.
				this.isShowTestResultModal = true;
			}
		},
		appInfo() {
			this.initShowParams(); // whenever appInfo changes, meaning we just finished loaded service info, we should execute initShowParams
		},
	},
	computed: {
		...mapGetters([
			editNode.fullPath,
			nodeType.fullPath,
			isWebhookGetter.fullPath,
			isScheduledWebhookGetter.fullPath,
			isBranchesGetter.fullPath,
			isEmailTemplatesGetter.fullPath,
			isBranchesConsolidateDataNodeGetter.fullPath,
			editNodeParams.fullPath,
			editNodeParamsId.fullPath,
			getNodeTestResult.fullPath,
			getNode.fullPath,
			getService.fullPath,
			getActionParams.fullPath,
			rootNode.fullPath,
			actionContextNode.fullPath,
			isNativeElement.fullPath,
			loadingTestAndContinueGetter.fullPath,
			getIfActionHasDynamicParams.fullPath,
			editedWorkflowVersion.fullPath,
			isOfTypeCloud.fullPath,
			elementContextualData.fullPath,
			getAction.fullPath,
			isNodeDataLoading.fullPath,
			isTestingProfile.fullPath,
			getAccountConnection.fullPath,
			getConfigurationFieldResponse.fullPath,
			isConfigurationsOn.fullPath,
			getConfigurationAndInterfaceIdByFieldUUID.fullPath,
			getNodeOutputLabelsByNodeId.fullPath,
			isOfTypeFeature.fullPath,
			isOfTypeTool.fullPath,
			selectedWorkflowId.fullPath,
			canEditWorkflow.fullPath,
		]),
		...mapGettersShorthand({
			editWorkflow,
			isCustomerAuthOn,
			isSendNotification,
			notification,
			isTriggerNode,
			flatPreviousSteps,
			isScheduledWebhookGetter,
			element,
			getPlacement,
			getContext,
			useIsRootNode,
		}),
		triggerConfig() {
			return this.editWorkflow.trigger_config;
		},
		currentlySelectedAction() {
			if (this[editNode.fullPath].action) {
				return this[getAction.fullPath](this[editNode.fullPath].action);
			}
			return null;
		},
		skipAuthAction() {
			return this.currentlySelectedAction?.skip_auth;
		},
		isNodeDataLoading() {
			return this[isNodeDataLoading.fullPath];
		},
		isCloudFlow() {
			return this[isOfTypeCloud.fullPath];
		},
		isWebhook() {
			return this[isWebhookGetter.fullPath];
		},
		isScheduledWebhook() {
			return this[isScheduledWebhookGetter.fullPath];
		},
		isEmailTemplates() {
			return this[isEmailTemplatesGetter.fullPath];
		},
		loadingTestAndContinue() {
			return this[loadingTestAndContinueGetter.fullPath];
		},
		relevantEditNode() {
			if (this.hasContextualDataFromElement) {
				// In case on context extracted without contextNode - only for isWebhook nodes.
				return this[rootNode.fullPath];
			}
			return this[editNode.fullPath];
		},
		actionContextNode() {
			return this[actionContextNode.fullPath];
		},
		elementContextualData() {
			return this[elementContextualData.fullPath] || {};
		},
		hasContextualDataFromElement() {
			// Don't remove the check for isWebhook ! so we know we are in the rootNode of a contextual action
			// Check that there isn't actionContextNode, and there is elementContextualData data
			return !this.actionContextNode && Object.keys(this.elementContextualData).length > 0;
		},
		editNodeTestResponse() {
			const { id: nodeId } = this.relevantEditNode;
			const testResponseOutputLabels = this[getNodeOutputLabelsByNodeId.fullPath](nodeId);
			const testResult = this[getNodeTestResult.fullPath](nodeId);
			return WorkflowNodeService.replaceKeysWithOutputLabels(testResult, testResponseOutputLabels);
		},
		testResponse() {
			if (this.hasContextualDataFromElement) {
				return this.elementContextualData;
			}
			if (this[editNode.fullPath] && this.relevantEditNode) {
				return flattenPreserveArrays(this.editNodeTestResponse);
			}
			return {};
		},
		testResultStatus() {
			if (this.hasContextualDataFromElement) {
				return TEST_RESULT_STATUSES.SUCCESS;
			}
			const node = this.getSourceOfRelevantNode();
			return node ? node.test_result_status : null;
		},
		appInfo() {
			if (this.hasContextualDataFromElement) {
				return this[getService.fullPath](this.getPlacement(this.element.placement).service);
			}
			return this[getService.fullPath](this.relevantEditNode?.service);
		},
		serviceVersion() {
			return this.relevantEditNode?.service_version;
		},
		canContinue() {
			if (this.hasContextualDataFromElement) {
				return true;
			}
			const propertiesOfNodeAreDefined = (!!this.relevantEditNode?.service && !!this.relevantEditNode?.action) || this.isScheduledWebhook;
			// we get the original state of node (the one synced with the server) to make sure
			// the server already knows what are the credentials of the current relevant node
			const node = this.getSourceOfRelevantNode();
			const nodeHasValidCredentials = WorkflowNodeService.checkIfCredentialsAreValidForNode(
				this[editedWorkflowVersion.fullPath],
				this.appInfo,
				node,
				this.skipAuthAction,
				this[getAccountConnection.fullPath],
				this[getConfigurationFieldResponse.fullPath],
				this[isConfigurationsOn.fullPath]
			);
			return propertiesOfNodeAreDefined && nodeHasValidCredentials && !this.isShowSelectApp;
		},
		actionParams() {
			return this[getActionParams.fullPath](this.relevantEditNode?.action);
		},
		allRequiredFieldsFilled() {
			const {
				actionHasDynamicParams,
				dynamicParamsOfCurrentNode,
				waitingForDynamicFields,
				actionParams,
				isWebhook,
				isSendNotification,
				notification,
				relevantEditNode,
			} = this;
			return BlockEditorService.allRequiredFieldsFilled(
				actionHasDynamicParams,
				dynamicParamsOfCurrentNode,
				waitingForDynamicFields,
				this[editNodeParams.fullPath],
				actionParams,
				isWebhook,
				this.element,
				this.getContext(this.element?.context),
				this[isNativeElement.fullPath],
				isSendNotification,
				notification,
				relevantEditNode,
				this[getConfigurationAndInterfaceIdByFieldUUID.fullPath],
				this[getConfigurationFieldResponse.fullPath],
				this[isConfigurationsOn.fullPath]
			);
		},
		hasEditPermissions() {
			return this[canEditWorkflow.fullPath](this[selectedWorkflowId.fullPath]);
		},
		btnDisabled() {
			if (!this.hasEditPermissions) {
				return true;
			}
			if (this[isNodeDataLoading.fullPath] || this[isTestingProfile.fullPath]) {
				return true;
			}
			if (this.isShowParams) {
				return !this.allRequiredFieldsFilled || this.waitingForDynamicFields;
			} else {
				if (this.isParameterizedWebhook || this.isCloudFlow) {
					return false;
				}
				return !(this.canContinue && this.allRequiredFieldsFilled);
			}
		},
		waitingForDynamicFields() {
			if (this.actionHasDynamicParams) {
				if (this.dynamicParamsOfCurrentNode) {
					const editNodeParamsLoaded = this[editNodeParams.fullPath].length > 0;
					return !editNodeParamsLoaded;
				}
				return true;
			}
			return false;
		},
		actionHasDynamicParams() {
			return this[getIfActionHasDynamicParams.fullPath](this.relevantEditNode?.action);
		},
		dynamicParamsOfCurrentNode() {
			return this[editNodeParamsId.fullPath] === this.relevantEditNode?.id;
		},
		configurationApproved() {
			return this[editNode.fullPath].configuration_approved;
		},
		showBottomBar() {
			return !this[isOfTypeTool.fullPath] || WorkflowToolService.isURLTool(this.editWorkflow);
		},
		isParameterizedWebhook() {
			return !this.element && !this.isCloudFlow;
		},
		isTestError() {
			return (
				this.testResponse.hasOwnProperty('error.message') ||
				this.testResponse.hasOwnProperty('error') ||
				this.testResponse.hasOwnProperty('Error') ||
				this.testResultStatus === TEST_RESULT_STATUSES.ERROR
			); // TODO change this to field 'error'
		},
		showTestResultPopUp() {
			return !this.loadingTestAndContinue && (this.relevantEditNode || this.hasContextualDataFromElement) && this.isShowTestResultModal;
		},
	},
	methods: {
		...mapActions([
			selectedNodeTestStep.fullPath,
			selectedNodeSkipTest.fullPath,
			selectedNodeTriggerSetup.fullPath,
			getActionsOfApp.fullPath,
			getNodeData.fullPath,
			nodeTestStep.fullPath,
			nodeSkipTest.fullPath,
			setSideBarState.fullPath,
			setEditNodeID.fullPath,
		]),
		...mapActionsShorthand({ getWorkflowWaitingForSampleData }),

		/* Automation sample data state */
		async getSampleDataState() {
			this.isWaitingForSampleData = await this.getWorkflowWaitingForSampleData({
				id: this.editWorkflow.id,
				waiting_type: WAITING_FLAG_TYPES.AUTOMATION_SAMPLE,
			});
			if (this.isWaitingForSampleData) {
				this.startWaitingSampleDataTimer();
			}
		},
		startWaitingSampleDataTimer() {
			clearTimeout(this.timer);
			this.timer = setTimeout(() => {
				this.isWaitingForSampleData = false;
			}, 1000 * 60 * 3);
		},
		hideTestCard() {
			this.isShowTestResultModal = false;
		},
		skipTestNode() {
			this[nodeSkipTest.fullPath]({ id: this[actionContextNode.fullPath].id });
		},
		setIsShowParams(newV) {
			if (!newV && this.isShowParams == null) {
				setTimeout(() => {
					this.isShowAnimation = true;
				}, 500);
			}
			this.isShowParams = newV;
		},
		async testAndContinue(testValues) {
			const isAutomationTrigger = this.isTriggerNode && this.triggerConfig?.subscribed && !this.isScheduledWebhook;
			if (isAutomationTrigger) {
				return this.triggerSetup();
			}
			setTimeout(() => {
				this.isShowTestResultModal = true;
			}, 200);

			if (this.isWebhook) {
				if (this.isParameterizedWebhook) {
					await this[selectedNodeTestStep.fullPath](testValues);
				} else {
					await this[selectedNodeTestStep.fullPath]();
				}
			} else {
				await this[selectedNodeTestStep.fullPath]();
			}
		},
		async triggerSetup() {
			this.isBtnFuncLoading = true;
			this.isTriggerSetupLoading = true;
			try {
				await this[selectedNodeTriggerSetup.fullPath]()
					.catch((err) => {
						this.$toast.error('Error occurred while saving trigger: ' + err.response?.data?.error?.msg);
					})
					.finally(() => {
						this.isBtnFuncLoading = false;
					});
				this.isWaitingForSampleData = true;
				await this.getSampleDataState(); // Sync it with timer etc.
			} finally {
				this.isTriggerSetupLoading = false;
				this.hideTestCard();
			}
		},
		initShowParams() {
			if (this.isTriggerNode && this.triggerConfig?.subscribed) {
				this.getSampleDataState();
				this.setIsShowParams(true);
			} else if (this.isSendNotification) {
				this.setIsShowParams(true);
			} else {
				const nodeHasValidCredentials = WorkflowNodeService.checkIfCredentialsAreValidForNode(
					this[editedWorkflowVersion.fullPath],
					this.appInfo,
					this[editNode.fullPath],
					this.skipAuthAction,
					this[getAccountConnection.fullPath],
					this[getConfigurationFieldResponse.fullPath],
					this[isConfigurationsOn.fullPath]
				);
				this.isShowParams = this.configurationApproved && nodeHasValidCredentials;
			}
		},
		async btnFunc() {
			if (this.isParameterizedWebhook) {
				const hasParams = !isEmpty(this.relevantEditNode.params_map.url_parameters);
				try {
					const payload =
						hasParams || this.isCustomerAuthOn
							? await openTestURLToolDialog({ workflowId: this.editWorkflow.id, mode: 'trigger-step' })
							: {};
					this.applyTest({ params_map: { url_parameters: payload } });
				} catch (err) {
					if (err === 'cancel') {
						// user canceled the dialog
						return;
					}
					throw err;
				}
			} else if (this.isTriggerNode && this.isShowParams && !this.isScheduledWebhook) {
				this.triggerSetup();
			} else if (this.isScheduledWebhook || this.isShowParams || this.isWebhook) {
				this.testAndContinue();
			}
		},
		getSourceOfRelevantNode() {
			// we might want to do some validations on the node object came from server and not the one in the local state
			if (this.relevantEditNode) {
				const { id: nodeId } = this.relevantEditNode;
				const node = this[getNode.fullPath](nodeId);
				return node;
			}
			return null;
		},
		applyTest(payload = {}) {
			this.testAndContinue(payload);
		},
		async applyTestAgain() {
			await this.btnFunc();
		},
	},
};
</script>

<style scoped lang="scss">
@import '@/components/WidgetForm/widget-form-mixins.scss';

@import './block-editor.scss';

$overlay-z-index: 50;

.narrow-widget-max-width {
	max-width: 600px;
	&.is-wide {
		max-width: 1600px;
	}
}

.block-editor-container {
	.webhook-container {
		height: calc(100% - 65px);
		position: relative;
		z-index: $overlay-z-index - 5;
		overflow: auto;
	}
}
</style>
